import { DownloadOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Drawer,
  Flex,
  Segmented,
  Skeleton,
  Typography
} from "antd"
import React, { Suspense, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { LazyPdfPages } from "../../../../components/Esign/index.ts"
import { certificateName } from "../../../../components/Esign/signing/types.ts"
import {
  getEnvelopeCertificate,
  getEnvelopeDocument,
  saveEnvelopeCertificate,
  saveEnvelopeDocument
} from "../../api/esign.services.ts"
import type { TrackedEnvelope } from "../../types/tracking.types.ts"

const { Text } = Typography

type View = "document" | "certificate"

type Shown =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "unreadable" }
  | { state: "ready"; bytes: Uint8Array }

interface Props {
  envelope: TrackedEnvelope
  onClose: () => void
}

const pdfName = (name: string) => (/\.pdf$/i.test(name) ? name : `${name}.pdf`)

/** The document as it stands — every signature so far, sealed once completed — drawn in the app. */
export function SignedDocumentDrawer({ envelope, onClose }: Props) {
  const { t } = useTranslation()
  const completed = envelope.status === "completed"
  const [view, setView] = useState<View>("document")
  const [shown, setShown] = useState<Shown>({ state: "loading" })
  const [attempt, setAttempt] = useState(0)
  const [saving, setSaving] = useState(false)
  const generation = useRef(0)

  const signers = envelope.recipients.filter(
    (recipient) => recipient.role === "signer"
  )
  const signed = signers.filter(
    (recipient) => recipient.status === "signed"
  ).length

  useEffect(() => {
    const mine = ++generation.current
    setShown({ state: "loading" })
    const load =
      view === "certificate" ? getEnvelopeCertificate : getEnvelopeDocument
    void load(envelope.id)
      .then((result) => {
        if (mine !== generation.current) return
        setShown(
          result.ok && result.data
            ? { state: "ready", bytes: result.data }
            : { state: "failed" }
        )
      })
      .catch(() => {
        if (mine === generation.current) setShown({ state: "failed" })
      })
    return () => {
      generation.current += 1
    }
  }, [envelope.id, view, attempt])

  const download = async () => {
    setSaving(true)
    const name = pdfName(envelope.subject.name)
    if (view === "certificate")
      await saveEnvelopeCertificate(envelope.id, certificateName(name))
    else await saveEnvelopeDocument(envelope.id, name)
    setSaving(false)
  }

  const retry = (
    <Button size={"small"} onClick={() => setAttempt((count) => count + 1)}>
      {t("esign.tracking.viewer.retry")}
    </Button>
  )

  return (
    <Drawer
      open
      onClose={onClose}
      width={"min(920px, 100vw)"}
      title={
        <Flex vertical>
          <Text strong ellipsis={{ tooltip: envelope.subject.name }}>
            {envelope.subject.name}
          </Text>
          <Text type={"secondary"} className={"text-xs font-normal"}>
            {completed
              ? t("esign.tracking.viewer.completed")
              : t("esign.tracking.viewer.signedSoFar", {
                  signed,
                  total: signers.length
                })}
          </Text>
        </Flex>
      }
      extra={
        <Button
          icon={<DownloadOutlined />}
          loading={saving}
          onClick={() => void download()}
        >
          {t("esign.tracking.viewer.download")}
        </Button>
      }
    >
      <Flex vertical gap={"middle"}>
        {completed && (
          <Segmented<View>
            value={view}
            onChange={setView}
            options={[
              { value: "document", label: t("esign.tracking.viewer.document") },
              {
                value: "certificate",
                label: t("esign.tracking.viewer.certificate")
              }
            ]}
          />
        )}
        {shown.state === "loading" && (
          <div aria-busy aria-label={t("esign.tracking.viewer.loading")}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        )}
        {shown.state === "failed" && (
          <Alert
            type={"error"}
            showIcon
            message={t("esign.tracking.viewer.failed")}
            action={retry}
          />
        )}
        {shown.state === "unreadable" && (
          <Alert
            type={"warning"}
            showIcon
            message={t("esign.tracking.viewer.unreadable")}
            action={retry}
          />
        )}
        {shown.state === "ready" && (
          <Suspense fallback={<Skeleton active paragraph={{ rows: 10 }} />}>
            <LazyPdfPages
              key={`${envelope.id}-${view}-${attempt}`}
              data={shown.bytes}
              onError={() => setShown({ state: "unreadable" })}
            />
          </Suspense>
        )}
      </Flex>
    </Drawer>
  )
}
